// PaymentDetailsTab.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { PaymentLinkCard } from './PaymentDetailsTab/PaymentLinkCard';
import { CancelPaymentModal } from './PaymentDetailsTab/CancelPaymentModal';
import { usePaymentLinkModal } from 'hooks/usePaymentLinkModal';
import { toast } from 'sonner';
import { PaymentHeader } from './PaymentDetailsTab/PaymentHeader';
import { usePaymentLinks } from 'hooks/usePaymentLinks';
import { EmptyPaymentState } from './PaymentDetailsTab/EmptyPaymentState';
import { PaymentLinkModal } from './PaymentDetailsTab/PaymentLinkModal';
import { getLeadDetails } from 'services/applicantService';
import { getInvoiceData } from 'services/paymentServices';
import { jsPDF } from 'jspdf';
import html2canvasPro from 'html2canvas-pro';
import { PaidInvoiceTemplate } from './PaymentDetailsTab/PaymentLinkModal/PaidInvoiceTemplate';
import { useRef } from 'react';

const PaymentDetailsTab = ({ isFeePayment, isMobile, progressData = {}, refreshProgressJourney, refreshLeadData, leadId, leadData }) => {
  // Remove useParams since leadId is now passed as a prop
  // const { id: leadId } = useParams();
  // Custom hooks for state management
  const {
    transactions,
    loading,
    syncing,
    transactionSyncing,
    loadPaymentLinks,
    handleSyncAll,
    handleSyncTransaction,
    handleCancelLink,
    refreshPaymentLinksSilently,
  } = usePaymentLinks(leadId, refreshProgressJourney);
  const [isInternational, setIsInternational] = useState(false);

  const paymentLinkModal = usePaymentLinkModal(leadId, refreshProgressJourney, refreshPaymentLinksSilently, isFeePayment, isInternational, refreshLeadData);

  // Cancel modal state
  const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false);
  const [cancelConfirmState, setCancelConfirmState] = useState('pending');
  const [selectedLinkToCancel, setSelectedLinkToCancel] = useState(null);
  const [isCancellingLink, setIsCancellingLink] = useState(null);
  
  // inside PaymentDetailsTab
  const [selectedGateway, setSelectedGateway] = useState(null);
  const [isDownloadingInvoice, setIsDownloadingInvoice] = useState(null); // stores PaymentLinkId
  const [invoiceData, setInvoiceData] = useState(null);
  const invoiceRef = useRef(null);

  const handleSelectGateway = async (gateway) => {
    setSelectedGateway(gateway); // update dropdown selection
    await loadPaymentLinks(gateway?.GatewayId || null); // hit API with selected gateway
  };

  // Segregate transactions based on IsEnrollmentFee flag
  const displayTransactions = useMemo(() => {
    if (!Array.isArray(transactions)) return [];
    
    if (isFeePayment) {
      // Fee Payment tab: show only enrollment fee links
      return transactions.filter(t => t.IsEnrollmentFee === true);
    } else {
      // Payment Details tab: show only non-enrollment fee links
      return transactions.filter(t => t.IsEnrollmentFee === false || t.IsEnrollmentFee === null || t.IsEnrollmentFee === undefined);
    }
  }, [transactions, isFeePayment]);

  const paidLinkExists = useMemo(
    () => displayTransactions.some(t => t.LinkStatus === 'PAID'),
    [displayTransactions]
  );

  // Derived state to check if accounts counsellor (Accounts User) is assigned
  const isAccountsAssigned = useMemo(() => {
    const counselorName = leadData?.accountsCounsellorName || 
                        leadData?.SourceDetails?.LeadAccountantCounsellorName || 
                        leadData?.accountsCounsellor || 
                        leadData?.AccountantCounsellorName;
    
    if (!counselorName) return false;
    const n = counselorName.toString().trim().toLowerCase();
    // Check against various unassigned indicators
    return !["", "n/a", "unassigned", "null", "undefined"].includes(n);
  }, [leadData]);

  // Check if previous journey steps are completed
  const canCreatePaymentLink = useMemo(() => {
    if (isFeePayment) {
      // Fee Payment requires accounts counsellor to be assigned
      return isAccountsAssigned;
    }
    // Application fee payment link can be created without specific assignment
    return true;
  }, [isFeePayment, isAccountsAssigned]);

  // Get descriptive message for why payment link cannot be created
  const paymentLinkBlockedReason = useMemo(() => {
    if (isFeePayment) {
      if (!isAccountsAssigned) {
        return "Assign Accounts User before creating link";
      }
    }
    return null;
  }, [isFeePayment, isAccountsAssigned]);

  useEffect(() => {
    if (leadId) {
      loadPaymentLinks();
      // Fetch lead details to determine international status
      const fetchLeadDetails = async () => {
        try {
          const res = await getLeadDetails(leadId);
          if (res.success) {
            const data = res.data || {};
            // Check IsInternationalLead flag or nationality (assume 1 is India)
            const isIntl = data.BasicInformation?.IsInternationalLead === true || 
                          data.IsInternationalLead === true ||
                          (data.BasicInformation?.NationalityId !== 1 && data.BasicInformation?.NationalityId !== undefined && data.BasicInformation?.NationalityId !== null) ||
                          (data.NationalityId !== 1 && data.NationalityId !== undefined && data.NationalityId !== null);
            setIsInternational(isIntl);
          }
        } catch (err) {
          console.error('Error fetching lead details for international check:', err);
        }
      };
      fetchLeadDetails();
    }
  }, [leadId]);

  const handleOpenPaymentLinkModal = () => {
    // Check if previous journey steps are completed
    if (!canCreatePaymentLink) {
      toast.error(paymentLinkBlockedReason || 'Please complete previous steps in the journey before generating a payment link.');
      return;
    }

    const hasActiveLink = displayTransactions.some(txn => txn.LinkStatus === 'ACTIVE');

    if (hasActiveLink) {
      toast.error('Cannot create a new payment link. An active payment link already exists.');
      return;
    }

    paymentLinkModal.handleOpen();
  };
  const handleOpenCancelConfirm = (transaction) => {
    if (transaction.LinkStatus !== 'ACTIVE') {
      toast.error('Only active payment links can be cancelled');
      return;
    }
    setSelectedLinkToCancel(transaction);
    setCancelConfirmState('pending');
    setShowCancelConfirmModal(true);
  };

  const handleConfirmCancelPayment = async () => {
    if (!selectedLinkToCancel) return;

    setIsCancellingLink(selectedLinkToCancel.PaymentLinkId);
    const result = await handleCancelLink(selectedLinkToCancel);

    if (result.success) {
      setCancelConfirmState('success');
      setTimeout(() => {
        setShowCancelConfirmModal(false);
        setSelectedLinkToCancel(null);
        setIsCancellingLink(null);
      }, 1500);
    } else {
      setIsCancellingLink(null);
    }
  };

  const handleCloseCancelConfirm = () => {
    if (cancelConfirmState !== 'pending') return;
    setShowCancelConfirmModal(false);
    setSelectedLinkToCancel(null);
    setIsCancellingLink(null);
  };

  const handleDownloadInvoice = async (transaction) => {
    if (!leadId) return;
    
    setIsDownloadingInvoice(transaction.PaymentLinkId);
    
    try {
      const res = await getInvoiceData(leadId);
      if (res.success && res.data) {
        setInvoiceData(res.data);
        
        // Wait for state update and template rendering
        setTimeout(async () => {
          if (!invoiceRef.current) {
            toast.error("Failed to generate invoice. Please try again.");
            setIsDownloadingInvoice(null);
            return;
          }

          try {
            const canvas = await html2canvasPro(invoiceRef.current, {
              scale: 2,
              useCORS: true,
              logging: false,
              backgroundColor: '#ffffff',
            });
            
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF({
              orientation: 'portrait',
              unit: 'px',
              format: [canvas.width / 2, canvas.height / 2],
            });
            
            pdf.addImage(imgData, 'PNG', 0, 0, canvas.width / 2, canvas.height / 2);
            pdf.save(`Invoice_${res.data.FullName || 'Student'}_${transaction.PaymentLinkId.substring(0, 8)}.pdf`);
            toast.success("Invoice downloaded successfully");
          } catch (pdfErr) {
            console.error('PDF generation error:', pdfErr);
            toast.error("Failed to generate PDF");
          } finally {
            setIsDownloadingInvoice(null);
            setInvoiceData(null);
          }
        }, 500);
      } else {
        toast.error(res.message || "Failed to fetch invoice data");
        setIsDownloadingInvoice(null);
      }
    } catch (err) {
      console.error('Invoice download error:', err);
      toast.error("Something went wrong while downloading invoice");
      setIsDownloadingInvoice(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Hidden layout for PDF generation */}
      {invoiceData && (
        <div className="fixed -left-[9999px] -top-[9999px]">
          <PaidInvoiceTemplate 
            ref={invoiceRef} 
            invoiceData={invoiceData}
            leadData={leadData}
          />
        </div>
      )}
      <PaymentHeader
        transactionCount={displayTransactions.length}
        syncing={syncing}
        loading={loading}
        isMobile={isMobile}
        paidLinkExists={paidLinkExists}
        canCreatePaymentLink={canCreatePaymentLink}
        paymentLinkBlockedReason={paymentLinkBlockedReason}
        gateways={paymentLinkModal.gateways}        // NEW
        selectedGateway={selectedGateway} // NEW
        onSelectGateway={handleSelectGateway}
        onRefresh={() => handleSyncAll(selectedGateway?.GatewayId || null)}
        onCreatePaymentLink={handleOpenPaymentLinkModal}
        isStudent={leadData?.IsStudent}
      />

      {displayTransactions.length === 0 ? (
        <EmptyPaymentState
          isMobile={isMobile}
          isFeePayment={isFeePayment}
          paidLinkExists={paidLinkExists}
          canCreatePaymentLink={canCreatePaymentLink}
          paymentLinkBlockedReason={paymentLinkBlockedReason}
          onCreatePaymentLink={handleOpenPaymentLinkModal}
          isStudent={leadData?.IsStudent}
        />
      ) : (
        <div className="space-y-2.5">
          {displayTransactions.map((transaction) => (
            <PaymentLinkCard
              key={transaction.PaymentLinkId}
              transaction={transaction}
              isFeePayment={isFeePayment}
              isMobile={isMobile}
              isCancelling={isCancellingLink === transaction.PaymentLinkId}
              isSyncing={!!transactionSyncing[transaction.LinkId]}
              onCancel={handleOpenCancelConfirm}
              onSync={handleSyncTransaction}
              onDownloadInvoice={handleDownloadInvoice}
              isDownloadingInvoice={isDownloadingInvoice === transaction.PaymentLinkId}
              isStudent={leadData?.IsStudent}
            />
          ))}
        </div>
      )}

      <PaymentLinkModal
        isFeePayment={isFeePayment}
        isOpen={paymentLinkModal.isOpen}
        paymentLinkStep={paymentLinkModal.paymentLinkStep}
        gateways={paymentLinkModal.gateways}
        isLoadingGateways={paymentLinkModal.isLoadingGateways}
        selectedGateway={paymentLinkModal.selectedGateway}
        paymentCatalogs={paymentLinkModal.paymentCatalogs}
        isLoadingCatalogs={paymentLinkModal.isLoadingCatalogs}
        selectedCatalog={paymentLinkModal.selectedCatalog}
        applicationForms={paymentLinkModal.applicationForms}
        isLoadingApplicationForms={paymentLinkModal.isLoadingApplicationForms}
        selectedApplicationForm={paymentLinkModal.selectedApplicationForm}
        paymentFormData={paymentLinkModal.paymentFormData}
        isCreatingLink={paymentLinkModal.isCreatingLink}
        createdLinkData={paymentLinkModal.createdLinkData}
        copySuccess={paymentLinkModal.copySuccess}
        handleClose={paymentLinkModal.handleClose}
        handleSelectGateway={paymentLinkModal.handleSelectGateway}
        handleSelectCatalog={paymentLinkModal.handleSelectCatalog}
        handleInputChange={paymentLinkModal.handleInputChange}
        handleBulkUpdate={paymentLinkModal.handleBulkUpdate}
        handleCreatePaymentLink={paymentLinkModal.handleCreatePaymentLink}
        handleCopyPaymentLink={paymentLinkModal.handleCopyPaymentLink}
        setPaymentLinkStep={paymentLinkModal.setPaymentLinkStep}
        setSelectedApplicationForm={paymentLinkModal.setSelectedApplicationForm}
        isMobile={isMobile}
        isInternational={isInternational}
        leadData={leadData}
        feeBreakup={paymentLinkModal.feeBreakup}
        setFeeBreakup={paymentLinkModal.setFeeBreakup}
      />

      <CancelPaymentModal
        show={showCancelConfirmModal}
        transaction={selectedLinkToCancel}
        confirmState={cancelConfirmState}
        isLoading={isCancellingLink !== null}
        onConfirm={handleConfirmCancelPayment}
        onClose={handleCloseCancelConfirm}
      />
    </div>
  );
};

export default PaymentDetailsTab;
